import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { CreditScoringForm } from '../components/CreditScoringForm';
interface CreditScore {
  id: string;
  name: string;
  description: string;
  totalScoreRange: number;
  createdAt: string;
  status: 'active' | 'inactive';
}
export function CreditScoringSetup() {
  const [view, setView] = useState<'list' | 'form'>('list');
  const [selectedScore, setSelectedScore] = useState<CreditScore | null>(null);
  // Mock data
  const [creditScores] = useState<CreditScore[]>([{
    id: 'CS-001',
    name: 'Standard Credit Score',
    description: 'Default scoring model for retail customers',
    totalScoreRange: 100,
    createdAt: '2024-01-15',
    status: 'active'
  }, {
    id: 'CS-002',
    name: 'Premium Credit Score',
    description: 'Enhanced scoring for high-value customers',
    totalScoreRange: 1000,
    createdAt: '2024-02-20',
    status: 'active'
  }, {
    id: 'CS-003',
    name: 'SME Credit Score',
    description: 'Specialized scoring for small business loans',
    totalScoreRange: 100,
    createdAt: '2024-03-10',
    status: 'inactive'
  }]);
  const handleAddNew = () => {
    setSelectedScore(null);
    setView('form');
  };
  const handleEdit = (score: CreditScore) => {
    setSelectedScore(score);
    setView('form');
  };
  const handleBack = () => {
    setView('list');
    setSelectedScore(null);
  };
  const handleSave = () => {
    // In real app, this would save to backend
    setView('list');
    setSelectedScore(null);
  };
  if (view === 'form') {
    return <CreditScoringForm creditScore={selectedScore} onBack={handleBack} onSave={handleSave} data-id="element-1978" />;
  }
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1979">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1980">
        <div data-id="element-1981">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1982">
            Credit Scoring Models
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1983">
            Manage credit scoring rules and configurations
          </p>
        </div>
        <Button onClick={handleAddNew} data-id="element-1984">
          <Plus className="h-4 w-4 mr-2" data-id="element-1985" />
          Add New Credit Score
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1986">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1987">
          <thead className="bg-gray-50" data-id="element-1988">
            <tr data-id="element-1989">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1990">
                Credit Score ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1991">
                Credit Score Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1992">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1993">
                Total Score Range
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1994">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1995">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1996">
            {creditScores.map(score => <tr key={score.id} className="hover:bg-gray-50 transition-colors" data-id="element-1997">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1998">
                  <span className="text-sm font-medium text-gray-900" data-id="element-1999">
                    {score.id}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-2000">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-2001">
                    {score.name}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-2002">
                  <span className="text-sm text-gray-600" data-id="element-2003">
                    {score.description}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-2004">
                  <span className="text-sm font-medium text-blue-600" data-id="element-2005">
                    {score.totalScoreRange}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-2006">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${score.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2007">
                    {score.status.charAt(0).toUpperCase() + score.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2008">
                  <div className="flex items-center gap-2" data-id="element-2009">
                    <button onClick={() => handleEdit(score)} className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors" title="Edit" data-id="element-2010">
                      <Edit className="h-4 w-4" data-id="element-2011" />
                    </button>
                    <button className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors" title="Delete" data-id="element-2012">
                      <Trash2 className="h-4 w-4" data-id="element-2013" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {creditScores.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-2014">
          <p className="text-gray-500" data-id="element-2015">
            No credit scoring models configured yet. Click "Add New Credit
            Score" to create one.
          </p>
        </div>}
    </div>;
}